import React from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { areasTransferApi } from "../api/AreasTransfer.api";

interface AreaTransferDeleteModalProps {
  show: boolean;
  onHide: () => void;
  areaTransfer?: {
    id: number;
    from?: { name: string };
    to?: { name: string };
    transferType: "Pickup" | "Drop" | "Pickup & Drop";
    amount: number;
  };
}

const AreaTransferDeleteModal: React.FC<AreaTransferDeleteModalProps> = ({
  show,
  onHide,
  areaTransfer,
}) => {
  if (!areaTransfer) return null;

  const [handleDeleteTransfer, { isLoading: isDeleting }] =
    areasTransferApi.useDeleteAreaTransferMutation();

  const onConfirm = async () => {
    await handleDeleteTransfer(areaTransfer.id);
    onHide();
  };

  const getTransferDetails = () => {
    const fromName = areaTransfer.from?.name || "Unknown";
    const toName = areaTransfer.to?.name || "Unknown";
    return `${fromName} → ${toName}`;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      className="delete-confirmation-modal"
    >
      <Modal.Header className="border-0 pb-0">
        <button
          type="button"
          className="btn-close"
          onClick={onHide}
          aria-label="Close"
          disabled={isDeleting}
        />
      </Modal.Header>

      <Modal.Body className="text-center pt-0 pb-4">
        <div className="d-flex justify-content-center mb-3">
          <div className="bg-danger bg-opacity-10 p-3 rounded-circle">
            <i className="fa fa-times-circle fa-3x text-danger"></i>
          </div>
        </div>

        <h4 className="fw-bold mb-3">Delete Area Transfer</h4>

        <div className="alert alert-warning d-flex align-items-center justify-content-center py-2">
          <i className="fa fa-exclamation-triangle me-2"></i>
          <div className="fs-6">This action cannot be undone.</div>
        </div>

        <div className="mb-0 mt-3">
          <p>Are you sure you want to delete the following area transfer?</p>
          <div className="p-3 bg-light rounded mb-3">
            <div className="d-flex flex-column text-start">
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">ID:</span>
                <span className="fw-semibold">{areaTransfer.id}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Route:</span>
                <span className="fw-semibold">{getTransferDetails()}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Type:</span>
                <span className="fw-semibold">{areaTransfer.transferType}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Amount:</span>
                <span className="fw-semibold">
                  ${areaTransfer.amount.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 d-flex justify-content-center gap-2">
        <Button
          variant="outline-secondary"
          onClick={onHide}
          disabled={isDeleting}
          className="px-4 btn-flat"
        >
          Cancel
        </Button>

        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={isDeleting}
          className="px-4 btn-flat"
        >
          {isDeleting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Deleting...
            </>
          ) : (
            "Delete Transfer"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AreaTransferDeleteModal;
